import React, { useEffect, useState } from 'react';
import { ArrowRightOutlined, CheckCircleOutlined } from '@ant-design/icons';
import ContactForm from '../components/shared/ContactForm';
import './home.css';

const STRINGS = {
  en: {
    languageToggle: 'Français',
    globalCtas: {
      start: 'Get started'
    },
    hero: {
      kicker: 'Retail command centre',
      title: 'Calm control for every shift.',
      subtitle: 'One dashboard keeps owners, cashiers, and shoppers on the same playbook.',
      badge: 'Now onboarding multi-shop retailers in under 48 hours',
      primaryCta: 'Book a live demo',
      secondaryCta: 'Get started',
      highlights: [
        'Owners view every till + shelf live',
        'Cashiers get intent-aware prompts',
        'Shoppers self-serve carts + QR codes'
      ]
    },
    heroPanels: {
      assistant: {
        title: 'Welcome back !',
        prompt: 'Where to next? Your rollout stays calm.',
        ctas: [
          { label: 'Sign in', href: '/login' },
          { label: 'Create account', href: '/register' }
        ],
        toggle: 'Switch to Français'
      },
      workspace: {
        tag: 'Studio services',
        title: 'Beyond software support',
        bullets: [
          'We mirror your aisles with modern storefronts.',
          'We wire kiosk flows, signage, and cashier shortcuts.',
          'We prep seasonal launch kits so every drop is ready.'
        ]
      },
      roles: [
        {
          label: 'Store owners',
          detail: 'Peek into sales, inventory, and alerts across every location in seconds.'
        },
        {
          label: 'Cashiers',
          detail: 'Queue-friendly prompts, quick holds, and richer context keep lines moving.'
        },
        {
          label: 'Shoppers',
          detail: 'Build lists, budgets, and QR carts without waiting for staff.'
        }
      ],
      quickAction: 'Need a walkthrough? Replies in under 24h.'
    },
    featuresHeading: 'Why choose KioStock',
    features: [
      {
        label: 'Oversight',
        title: 'Every location, one calm view',
        copy: 'Switch between stores, alerts, and staffing without juggling five tools.'
      },
      {
        label: 'Checkout autopilot',
        title: 'Intent-aware lanes',
        copy: 'Discounts, holds, and split payments surface before your team asks.'
      },
      {
        label: 'Customer rituals',
        title: 'Guided shopping',
        copy: 'Clients build QR-ready carts, budgets, and reminders on any device.'
      },
      {
        label: 'Living reports',
        title: 'Automatic close packets',
        copy: 'Finance-ready exports drop the moment doors close.'
      }
    ],
    timelineIntro: {
      kicker: 'Timeline',
      title: 'From first hello to first loyal fan',
      copy: 'Here is the four-step, human rollout from first hello to first loyal fan.'
    },
    timeline: [
      {
        title: 'Share your story',
        copy: 'Tell us about your shops so we map the rollout and assign a lead.'
      },
      {
        title: 'Train every role',
        copy: 'Owners, cashiers, and shoppers get EN/FR lessons and cheat sheets.'
      },
      {
        title: 'Seed your catalog',
        copy: 'We preload taxes, SKUs, and promos so the app opens ready.'
      },
      {
        title: 'Serve the first client',
        copy: 'Launch with live monitoring and instant close reports.'
      }
    ],
    quote: {
      subheading: 'We preload data, coach every role, and stay on call until your first calm shift ends.',
      highlights: [
        {
          title: 'What we cover',
          points: ['EN/FR coaching per role', 'Catalog, taxes, and promos imported', 'Custom automations + ecommerce setup']
        },
        {
          title: 'What you feel',
          points: ['Smooth multi-store launches', 'Real humans on WhatsApp/Email 24/7', 'Playbooks you can hand to staff']
        }
      ]
    },
    contact: {
      heading: 'Plan launch with our partner team',
      subheading: 'Drop a note and we reply with a rollout plan within a day.',
      highlights: [
        {
          title: 'Share',
          points: ['Shop count + roles', 'Launch timeline', 'Tools you already use']
        },
        {
          title: 'Get back',
          points: ['Timeline + pricing outline', 'Training plan in EN/FR', 'Support thread with a named lead']
        }
      ]
    },
    footer: {
      line: 'Crafted for modern retailers, everywhere.',
      login: 'Sign in',
      contact: 'Get in touch'
    }
  },
  fr: {
    languageToggle: 'English',
    globalCtas: {
      start: 'Commencer'
    },
    hero: {
      kicker: 'Poste de commande retail',
      title: 'Le calme pour chaque shift.',
      subtitle: 'Un seul tableau de bord garde dirigeants, caissiers et clients sur la même feuille de route.',
      badge: 'Déploiement multi-boutiques en moins de 48 h',
      primaryCta: 'Planifier une démo',
      secondaryCta: 'Commencer',
      highlights: [
        'Dirigeants voient chaque caisse en direct',
        'Caissiers reçoivent des actions anticipées',
        'Clients gèrent paniers et QR sans aide'
      ]
    },
    heroPanels: {
      assistant: {
        title: 'Ravi de vous revoir',
        prompt: 'Où continuer ? On garde le déploiement serein.',
        ctas: [
          { label: 'Se connecter', href: '/login' },
          { label: 'Créer un compte', href: '/register' }
        ],
        toggle: 'Passer en English'
      },
      workspace: {
        tag: 'Services studio',
        title: 'Au-delà du logiciel',
        bullets: [
          'Nous créons des vitrines en ligne calquées sur vos rayons.',
          'Nous installons kiosques, signalétique et raccourcis caisse.',
          'Nous préparons des kits saisonniers prêts à lancer.'
        ]
      },
      roles: [
        {
          label: 'Dirigeants & propriétaires',
          detail: 'Surveillez ventes, stocks et alertes de toutes vos enseignes en quelques secondes.'
        },
        {
          label: 'Caissiers',
          detail: 'Prompts de files, mises en attente rapides et contexte client libèrent les caisses.'
        },
        {
          label: 'Clients',
          detail: 'Listes, budgets et paniers QR sans patienter.'
        }
      ],
      quickAction: 'Besoin d’une démo ? Réponse sous 24 h.'
    },
    featuresHeading: 'Pourquoi choisir KioStock',
    features: [
      {
        label: 'Pilotage',
        title: 'Toutes vos enseignes, une vue',
        copy: 'Passez d’un magasin à l’autre, alertes et équipes sans jongler entre cinq outils.'
      },
      {
        label: 'Autopilote caisse',
        title: 'Actions anticipées',
        copy: 'Remises, mises en attente et paiements multiples apparaissent avant même la demande.'
      },
      {
        label: 'Rituels clients',
        title: 'Parcours guidés',
        copy: 'Les clients créent des paniers QR, budgets et rappels sur n’importe quel appareil.'
      },
      {
        label: 'Rapports vivants',
        title: 'Clôtures automatiques',
        copy: 'Exports prêts pour la compta envoyés dès la fermeture.'
      }
    ],
    timelineIntro: {
      kicker: 'Parcours',
      title: 'Du premier message au premier fan',
      copy: 'Voici le parcours guidé en quatre étapes du premier message au premier client fidèle.'
    },
    timeline: [
      {
        title: 'Partagez votre contexte',
        copy: 'Présentez vos boutiques pour que l’on trace la feuille de route et attribue un coach.'
      },
      {
        title: 'Former chaque rôle',
        copy: 'Dirigeants, caissiers et clients reçoivent modules FR/EN et antisèches.'
      },
      {
        title: 'Précharger le catalogue',
        copy: 'Nous préchargeons taxes, produits et promos pour ouvrir l’app opérationnelle.'
      },
      {
        title: 'Servir le premier client',
        copy: 'Lancement accompagné, monitoring live et rapports automatiques juste après la fermeture.'
      }
    ],
    quote: {
      subheading: 'Nous préchargeons vos données, coachons chaque rôle et restons disponibles jusqu’à la fin de votre premier shift serein.',
      highlights: [
        {
          title: 'Ce que nous prenons',
          points: ['Coaching FR/EN par rôle', 'Catalogues, taxes et promos importés', 'Automations et ecommerce configurés']
        },
        {
          title: 'Ce que vous vivez',
          points: ['Déploiements multi-magasins fluides', 'Humains sur WhatsApp/Email 24/7', 'Playbooks prêts à partager']
        }
      ]
    },
    contact: {
      heading: 'Planifiez le lancement avec notre équipe partenaire',
      subheading: 'Écrivez-nous et nous revenons avec un plan de déploiement sous 24 h.',
      highlights: [
        {
          title: 'À partager',
          points: ['Nombre de boutiques + rôles', 'Fenêtre de lancement', 'Outils déjà en place']
        },
        {
          title: 'Vous recevez',
          points: ['Planning + budget estimatif', 'Programme de formation FR/EN', 'Canal direct avec un référent']
        }
      ]
    },
    footer: {
      line: 'Imaginé pour les commerces modernes, partout.',
      login: 'Se connecter',
      contact: 'Nous contacter'
    }
  }
};

const useReveal = (dependencyKey) => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    const elements = document.querySelectorAll('[data-reveal]');
    elements.forEach((el) => {
      el.classList.remove('is-visible');
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [dependencyKey]);
};

const HomeLayout = () => {
  const [language, setLanguage] = useState('en');
  useReveal(language);
  const t = STRINGS[language];
  const handleScrollToContact = () => {
    const section = document.getElementById('contact');
    section?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="home">
      <div className="home__backdrop" />

      <section className="hero" data-reveal>
        <div className="hero__content">
          <span className="hero__kicker">{t.hero.kicker}</span>
          <h1 className="gradient-title">{t.hero.title}</h1>
          <p>{t.hero.subtitle}</p>
          <div className="hero__ctas">
            <button type="button" className="primary-btn" onClick={handleScrollToContact}>
              {t.hero.primaryCta}
            </button>
            <a href="/register" className="ghost-btn">
              {t.hero.secondaryCta} <ArrowRightOutlined />
            </a>
          </div>
          <div className="hero__badge">{t.hero.badge}</div>
          <div className="hero__highlights">
            {t.hero.highlights.map((highlight) => (
              <div key={highlight} className="hero__highlight-row">
                <CheckCircleOutlined />
                <span>{highlight}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="hero__visual">
          <div className="hero__panels">
            <div className="hero-card hero-card--assistant">
              <div className="hero-card__label">{t.heroPanels.assistant.title}</div>
              <p>{t.heroPanels.assistant.prompt}</p>
              <div className="hero-card__actions">
                {t.heroPanels.assistant.ctas.map((cta) => (
                  <a key={cta.label} href={cta.href} className="hero-card__button">
                    {cta.label}
                  </a>
                ))}
              </div>
              <button
                type="button"
                className="language-pill hero-card__toggle"
                onClick={() => setLanguage(language === 'en' ? 'fr' : 'en')}
              >
                {t.heroPanels.assistant.toggle}
              </button>
            </div>
            <div className="hero-card hero-card--workspace">
              <div className="hero-card__label">{t.heroPanels.workspace.tag}</div>
              <h4>{t.heroPanels.workspace.title}</h4>
              <ul>
                {t.heroPanels.workspace.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            </div>
            <div className="hero-card hero-card--roles">
              {t.heroPanels.roles.map((role) => (
                <div key={role.label} className="hero-roles__item">
                  <span>{role.label}</span>
                  <p>{role.detail}</p>
                </div>
              ))}
            </div>
            <div className="hero__chip">{t.heroPanels.quickAction}</div>
          </div>
        </div>
      </section>

      <div className="section-heading" data-reveal>
        <h2 className="gradient-title">{t.featuresHeading}</h2>
      </div>

      <section className="feature-grid" data-reveal>
        {t.features.map((feature) => (
          <article key={feature.title} className="veil-card feature-card">
            <span className="feature-card__label">{feature.label}</span>
            <h3>{feature.title}</h3>
            <p>{feature.copy}</p>
          </article>
        ))}
      </section>

      <section className="timeline-hub" data-reveal>
        <div className="timeline" id="stories">
          <div className="timeline__text">
            <span className="workflow__kicker">{t.timelineIntro.kicker}</span>
            <h2 className="gradient-title">{t.timelineIntro.title}</h2>
            <p>{t.timelineIntro.copy}</p>
          </div>
          <div className="timeline__stack">
            {t.timeline.map((step, index) => (
              <article key={step.title} className="timeline__card veil-card">
                <div className="timeline__index">0{index + 1}</div>
                <h4>{step.title}</h4>
                <p>{step.copy}</p>
              </article>
            ))}
          </div>
        </div>
        <div id="contact" className="contact contact--inline">
          <div className="contact__panel veil-card">
            <h2 className="gradient-title">{t.contact.heading}</h2>
            <p>{t.contact.subheading}</p>
            <div className="contact__highlights">
              {t.contact.highlights.map((block) => (
                <div key={block.title}>
                  <strong>{block.title}</strong>
                  <ul>
                    {block.points.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          <div className="contact__form veil-card">
            <ContactForm language={language} />
          </div>
        </div>
      </section>

      <section className="quote" data-reveal>
        <p>{t.quote.subheading}</p>
        <div className="quote__grid">
          {t.quote.highlights.map((section) => (
            <div key={section.title} className="quote__card">
              <strong>{section.title}</strong>
              <ul>
                {section.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <footer className="home-footer" data-reveal>
        <div className="footer-inner">
          <span>© {new Date().getFullYear()} KioStock. {t.footer.line}</span>
          <div className="footer-links">
            <a href="/login">{t.footer.login}</a>
            <a href="#contact">{t.footer.contact}</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomeLayout;